import { useContext, useEffect, useRef, useState } from "react"
import { useMsal } from "@azure/msal-react"
import { ChevronRightIcon } from "@chakra-ui/icons"
import {
  Modal,
  ModalOverlay,
  ModalContent,
  Alert,
  Flex,
  AlertTitle,
  AlertDescription,
  Button,
} from "@chakra-ui/react"

import { H4 } from "components/common"
import { ErrorIcon } from "components/ErrorIcon"

import { AuthContext } from "context/AuthContext"
import { ClientContext } from "context/ClientContext"
import { useErrorModal } from "context/ErrorContext"
import { UserContext } from "context/UserContext"

import { getSignInAuthority, parseMsalConfig } from "utilities/msalConfig"

export const ErrorModalAction = {
  LOGOUT: "logout",
  REFRESH: "refresh",
} as const

export type ErrorModalActionType =
  (typeof ErrorModalAction)[keyof typeof ErrorModalAction]

export type ErrorProps = {
  title?: string
  message?: string
  action?: ErrorModalActionType
  log?: string | Record<string, unknown>
}

const defaultTitle = "We're sorry! Something went wrong."
const defaultMessage = "We've encountered an unexpected error."

const AppErrorModal = () => {
  const { instance } = useMsal()
  const { client } = useContext(ClientContext)
  const { unsetTokens } = useContext(AuthContext)
  const { setUser } = useContext(UserContext)
  const { error, clearError } = useErrorModal()
  const [isLoading, setIsLoading] = useState(false)
  const [isLoggedOut, setIsLoggedOut] = useState(false)
  const logoutPerformedRef = useRef(false)

  const isAuthError = error?.action === ErrorModalAction.LOGOUT

  useEffect(() => {
    if (!error || !isAuthError || logoutPerformedRef.current) {
      return
    }

    const performLogout = async () => {
      logoutPerformedRef.current = true
      
      unsetTokens()
      setUser(undefined)

      if (instance) {
        await instance.clearCache()
      }

      setIsLoggedOut(true)
    }

    performLogout()
  }, [error, isAuthError, instance, unsetTokens, setUser])

  useEffect(() => {
    if (!error) {
      logoutPerformedRef.current = false
      setIsLoggedOut(false)
      setIsLoading(false)
    }
  }, [error])

  if (!error) return null

  if (error.log) {
    console.error(error.log)
  }

  const modalTitle = error.title ?? defaultTitle
  const modalMessage = error.message ?? defaultMessage

  const callToAction = isAuthError ? "Log back in" : "Refresh this page"

  const refreshPage = () => window.location.reload()

  const onAction = async () => {
    if (isAuthError) {
      setIsLoading(true)
      clearError()

      if (instance && client) {
        const config = parseMsalConfig(client)
        const authority = getSignInAuthority(config.authority)
        
        await instance.loginRedirect({
          scopes: ["openid", "offline_access"],
          authority,
          prompt: "login", // Force fresh login, don't use cached session
          redirectStartPage: window.location.origin,
          redirectUri: window.location.origin,
        })
      } else {
        // Fallback: refresh to trigger normal auth flow
        window.location.href = window.location.origin
      }
    } else {
      clearError()
      refreshPage()
    }
  }

  return (
    <Modal
      isOpen={!!error}
      onClose={onAction}
      closeOnOverlayClick={false}
      size="xl"
    >
      <ModalOverlay />
      <ModalContent
        mt={48}
        borderStyle="solid"
        borderColor="transparent"
        borderLeftWidth={0}
        borderBottom={0}
      >
        <Alert
          variant="left-accent"
          borderColor="error"
          background="white"
          borderRadius="md"
          borderLeftWidth={8}
          boxShadow="lg"
          alignItems="flex-start"
          py={6}
        >
          <ErrorIcon color="error" mr={3} mt="1px" boxSize={6} />
          <Flex direction="column">
            <AlertTitle color="errorText" as={H4}>
              {modalTitle}
            </AlertTitle>
            <AlertDescription
              mt={2}
            >{`${modalMessage} Please ${callToAction.toLowerCase()} to pick up where you left off.`}</AlertDescription>
            <Flex mt="4">
              <Button
                variant="link"
                rightIcon={<ChevronRightIcon />}
                onClick={onAction}
                isLoading={isLoading || (isAuthError && !isLoggedOut)}
                loadingText={isLoggedOut ? "Redirecting..." : "Signing out..."}
                isDisabled={isLoading || (isAuthError && !isLoggedOut)}
              >
                {callToAction}
              </Button>
            </Flex>
          </Flex>
        </Alert>
      </ModalContent>
    </Modal>
  )
}

export default AppErrorModal
